import React, { useEffect, useRef, useState } from "react";
import { Scale, BookOpen, FileText, RefreshCw } from "lucide-react";

/**
 * StatsBar — animated counters between Hero and "¿Por qué Pre-Legal.cl?".
 *
 * Numbers tween in once on first scroll-in (IntersectionObserver). Reduced-motion
 * users get the final value immediately. Each item supports an optional `prefix`
 * (e.g. "+") and `suffix`. Strings without a number ("Cada semana") are rendered
 * verbatim.
 */
const STATS = [
  {
    icon: Scale,
    value: 700,
    prefix: "+",
    label: "Fallos reales de tribunales chilenos",
  },
  {
    icon: BookOpen,
    value: 72,
    label: "Materias del derecho cubiertas",
  },
  {
    icon: FileText,
    value: 13000,
    prefix: "+",
    label: "Fragmentos de jurisprudencia indexados",
  },
  {
    icon: RefreshCw,
    text: "Cada semana",
    label: "Actualización de contenido",
  },
];

const formatChileanNumber = (n) =>
  String(Math.round(n)).replace(/\B(?=(\d{3})+(?!\d))/g, ".");

function useCountUp(target, { duration = 1400, start = false } = {}) {
  const [value, setValue] = useState(0);
  const rafRef = useRef(null);

  useEffect(() => {
    if (!start || target == null) return;
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
      setValue(target);
      return;
    }
    const t0 = performance.now();
    const tick = (now) => {
      const elapsed = Math.min(1, (now - t0) / duration);
      // ease-out-cubic
      const eased = 1 - Math.pow(1 - elapsed, 3);
      setValue(target * eased);
      if (elapsed < 1) rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => rafRef.current && cancelAnimationFrame(rafRef.current);
  }, [target, duration, start]);

  return value;
}

function StatItem({ stat, hasStarted }) {
  const Icon = stat.icon;
  const count = useCountUp(stat.value, { start: hasStarted });
  const display = stat.text
    ? stat.text
    : `${stat.prefix || ""}${formatChileanNumber(count)}${stat.suffix || ""}`;

  return (
    <div
      data-testid={`stat-${(stat.label || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 30)}`}
      className="flex flex-col items-center text-center px-3"
    >
      <div
        className="mb-3 inline-flex items-center justify-center"
        style={{
          width: 40,
          height: 40,
          borderRadius: 12,
          background: "var(--pl-primary-soft, rgba(37, 99, 235, 0.1))",
          color: "var(--pl-primary, #2563eb)",
        }}
        aria-hidden="true"
      >
        <Icon className="w-5 h-5" strokeWidth={2.2} />
      </div>
      <div
        className="font-extrabold tracking-tight"
        style={{
          fontSize: stat.text ? 26 : 36,
          lineHeight: 1.05,
          color: "var(--pl-primary, #2563eb)",
          letterSpacing: "-0.02em",
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {display}
      </div>
      <p
        className="mt-2 text-sm leading-snug max-w-[200px] mx-auto"
        style={{ color: "var(--pl-text-muted, #64748b)" }}
      >
        {stat.label}
      </p>
    </div>
  );
}

export default function StatsBar() {
  const [hasStarted, setHasStarted] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (typeof IntersectionObserver === "undefined") {
      setHasStarted(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            setHasStarted(true);
            io.disconnect();
            break;
          }
        }
      },
      { threshold: 0.35 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <section
      ref={ref}
      data-testid="stats-bar"
      className="py-12 px-5 sm:px-8"
      aria-label="Estadísticas de Pre-Legal.cl"
    >
      <div
        className="max-w-5xl mx-auto rounded-2xl px-6 py-8 sm:px-10 sm:py-10 grid grid-cols-2 lg:grid-cols-4 gap-y-8 gap-x-4"
        style={{
          background: "var(--pl-card)",
          border: "1px solid var(--pl-border)",
          boxShadow: "var(--pl-shadow-md, 0 8px 24px rgba(0, 0, 0, 0.32))",
        }}
      >
        {STATS.map((stat, i) => (
          <StatItem key={i} stat={stat} hasStarted={hasStarted} />
        ))}
      </div>
    </section>
  );
}
